import firebase from 'firebase/compat/app';
import LogManager from '../LogManager';

import 'firebase/compat/analytics';
import 'firebase/compat/app-check';

// How long a caller waits for an App Check token before going on without one.
const APP_CHECK_TOKEN_TIMEOUT_MS = 5000;

let appCheckActivated = false;

/**
 * App Check token for our own backend, sent as `X-Firebase-AppCheck`.
 *
 * Never throws: resolves to `undefined` when App Check was not activated (no
 * site key, or activation failed), the SDK cannot produce a token (reCAPTCHA
 * blocked, debug token not registered), or it takes longer than
 * APP_CHECK_TOKEN_TIMEOUT_MS. The caller then sends without the header and
 * lets the service answer.
 *
 * `forceRefresh` bypasses the SDK's cached token; use it when retrying after
 * the backend rejected the previous one.
 */
export async function getAppCheckToken({ forceRefresh = false } = {}) {
  if (!appCheckActivated) {
    console.warn('[firebase] App Check not activated (no site key, or activation failed) - no token to send');
    return undefined;
  }
  let timer;
  let timedOut = false;
  try {
    const tokenPromise = firebase.appCheck().getToken(forceRefresh);
    // Promise.race leaves a promise that rejects after the timeout unhandled.
    tokenPromise.catch((err) => {
      if (timedOut) console.warn('[firebase] App Check token failed after the timeout had already fired', err);
    });
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => {
        timedOut = true;
        resolve(undefined);
      }, APP_CHECK_TOKEN_TIMEOUT_MS);
    });
    const result = await Promise.race([tokenPromise, timeout]);
    if (!result) {
      console.warn(`[firebase] App Check token not ready within ${APP_CHECK_TOKEN_TIMEOUT_MS}ms - sending without it`);
      return undefined;
    }
    return result.token;
  } catch (err) {
    console.warn('[firebase] App Check token unavailable (non-fatal)', err);
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

export default class FirebaseManager {
  static instance = null;

  static getInstance(firebaseConfig) {
    if (!this.instance) {
      this.instance = new FirebaseManager(firebaseConfig);
    }
    return this.instance;
  }

  constructor(firebaseConfig) {
    if (!firebase.apps.length) {
      let FCM_API_KEY = 'AIzaSyCZnFc7Ea4BLfGTa044z3oYkuSD5UF2KuE',
        FCM_AUTH_DOMAIN = 'api-project-317780763450.firebaseapp.com',
        FCM_DATABASE_URL = 'https://api-project-317780763450.firebaseio.com',
        FCM_PROJECT_ID = 'api-project-317780763450',
        FCM_STORAGE_BUCKET = 'api-project-317780763450.appspot.com',
        FCM_MESSAGING_SENDER_ID = '317780763450',
        FCM_APP_ID = '1:317780763450:web:be60c3cf7662af679e6603',
        FCM_MESUREMENT_ID = 'G-XWYHJRQYMY',
        RECAPTCHA_SITE_KEY,
        APP_CHECK_DEBUG = false;
      if (firebaseConfig && typeof firebaseConfig === 'object') {
        ({
          FCM_API_KEY = 'AIzaSyCZnFc7Ea4BLfGTa044z3oYkuSD5UF2KuE',
          FCM_AUTH_DOMAIN = 'api-project-317780763450.firebaseapp.com',
          FCM_DATABASE_URL = 'https://api-project-317780763450.firebaseio.com',
          FCM_PROJECT_ID = 'api-project-317780763450',
          FCM_STORAGE_BUCKET = 'api-project-317780763450.appspot.com',
          FCM_MESSAGING_SENDER_ID = '317780763450',
          FCM_APP_ID = '1:317780763450:web:be60c3cf7662af679e6603',
          FCM_MESUREMENT_ID = 'G-XWYHJRQYMY',
          RECAPTCHA_SITE_KEY,
          APP_CHECK_DEBUG = false,
        } = firebaseConfig);
      }
      firebase.initializeApp({
        apiKey: FCM_API_KEY,
        authDomain: FCM_AUTH_DOMAIN,
        databaseURL: FCM_DATABASE_URL,
        projectId: FCM_PROJECT_ID,
        storageBucket: FCM_STORAGE_BUCKET,
        messagingSenderId: FCM_MESSAGING_SENDER_ID,
        appId: FCM_APP_ID,
        measurementId: FCM_MESUREMENT_ID,
      });

      // Optional and non-fatal: without a site key, or if activation fails,
      // everything else keeps working and getAppCheckToken() returns undefined.
      // The caller decides what "local" means; this module reads no host globals.
      if (RECAPTCHA_SITE_KEY) {
        try {
          if (APP_CHECK_DEBUG && typeof self !== 'undefined') {
            self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
          }
          // Pass a provider instance: a bare string key is taken as reCAPTCHA v3,
          // which silently fails every token exchange for an Enterprise key.
          firebase.appCheck().activate(new firebase.appCheck.ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY), true);
          appCheckActivated = true;
        } catch (err) {
          console.warn('[firebase] App Check activation failed (non-fatal)', err);
        }
      }

      // compat's logEvent reads `this._delegate`, so keep the original bound to
      // its instance; firebase.analytics() returns that same instance everywhere.
      const analytics = firebase.analytics();
      const rawLogfunction = analytics.logEvent.bind(analytics);
      analytics.logEvent = function () {
        const logManager = LogManager.getInstance();

        arguments[1] = processFirebaseEventParams(arguments[1]);
        logManager.log(`[FirebaseEvent] ${arguments[0]}:`, arguments[1]);
        rawLogfunction(...arguments);
      };
    }

    return firebase;
  }
}

function processFirebaseEventParams (params) {
  for (const key in params) {
    const value = params[key]
    if (value || value === 0) {
      const number = Number(value)
      if (!isNaN(number)) {
        params[key] = `_${value}`
      }
    }
  }
  return params
}